// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ZeroAddress } from 'ethers'
import * as yup from 'yup'
import { PoolTypeEnum, PoolTypeToString, yupErc20WeiRequired, yupErc20WeiSigned, yupEthAddrRequired } from './Enums'
import { sanitizeDisplayName } from './PoolOffchainDataCTO'
import { ArrayTypeEnum, EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ERC20Wei, ERC20WeiSigned } from './conversions'

export interface PortfolioSummaryCTO {
  accountAssets: ERC20Wei
  indicativeInterestRate: ERC20Wei
  gainLoss: ERC20WeiSigned
  gainLossInDay: ERC20WeiSigned
}
export const PortfolioSummaryCTOSchema = yup.object<PortfolioSummaryCTO>().shape({
  accountAssets: yupErc20WeiRequired
    .label('Account Assets')
    .required()
    .default('0')
    .meta(toFieldMetaData({ propName: 'accountAssets', description: 'Account Assets' })),
  indicativeInterestRate: yupErc20WeiRequired
    .label('Indicative Interest Rate')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'indicativeInterestRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 18,
        description: 'The target rate of return for a dynamic vault',
        stringType: StringTypeEnum.APR,
      })
    ),
  gainLoss: yupErc20WeiSigned
    .label('Interest Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLoss',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description: 'The total gain, loss, or interest accrued for the account to date. Includes debits and credits.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  gainLossInDay: yupErc20WeiSigned
    .label('Daily Interest Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLossInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description:
          'The total gain, loss, or interest accrued for the account for the day. Includes debits and credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
})
export interface PortfolioPoolCTO {
  poolAddr: EthAddr
  poolType: PoolTypeEnum
  vaultName: string
  displayName: string
  tokenBalance: ERC20Wei
  principalEarningInterest: ERC20Wei
  liquidityTokenSymbol: string
  indicativeInterestRate: ERC20Wei
  gainLoss: ERC20WeiSigned
  gainLossInDay: ERC20WeiSigned
}
export const PortfolioPoolCTOSchema = yup.object<PortfolioPoolCTO>().shape({
  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  vaultName: yup
    .string()
    .required()
    .label('Vault Name')
    .default(' ')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        propName: 'vaultName',
        description: 'Name of the Loan Vault',
      })
    ),
  displayName: yup
    .string()
    .required()
    .default(' ')
    .transform((value) => {
      return sanitizeDisplayName(value)
    })
    .label('Display Name')
    .meta(
      toFieldMetaData({
        propName: 'displayName',
        description:
          'A human-readable display name for the pool (optional). If not provided, the pool name will be used.',
      })
    ),
  tokenBalance: yupErc20WeiRequired
    .label('Vault Token Balance')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'tokenBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        description: 'The number of vault tokens held by the account. Debits and credits add/subtract from this value.',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description:
          'The current amount of loan principal earning the interestRate for this account. Debits and credits add/subtract from this value. Fees paid does not subtract from this value.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .required()
    .label('Symbol for Liquidity Token')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        description: 'Symbol for the Liquidity Token',
        example: 'USDC',
      })
    ),
  poolType: yup
    .number<PoolTypeEnum>()
    .oneOf(Object.values(PoolTypeEnum).filter((v) => typeof v === 'number') as number[])
    .label('Pool Type')
    .defined()
    .default(PoolTypeEnum.NotPool)
    .meta(
      toFieldMetaData({
        propName: 'poolType',
        description: 'Type 0 is a term pool, type 1 is a flexible pool, type 2 is a dynamic pool.',
        stateToString: PoolTypeToString,
      })
    ),
  indicativeInterestRate: yupErc20WeiRequired
    .label('Indicative Interest Rate')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'indicativeInterestRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 18,
        description: 'The target rate of return for a dynamic vault',
        stringType: StringTypeEnum.APR,
      })
    ),
  gainLoss: yupErc20WeiSigned
    .label('Interest Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLoss',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description: 'The total gain, loss, or interest accrued for the account to date. Includes debits and credits.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  gainLossInDay: yupErc20WeiSigned
    .label('Daily Interest Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLossInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description:
          'The total gain, loss, or interest accrued for the account for the day. Includes debits and credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
})
export interface PortfolioCTO {
  accountAddr: EthAddr

  timestamp: number
  timestampDateString: string

  dayNumber: number
  poolHoldings: PortfolioPoolCTO[]
  accountAssets: ERC20Wei
  portfolioSummaryIncludingAccount: PortfolioSummaryCTO
  portfolioSummaryExcludingAccount: PortfolioSummaryCTO
}
export const PortfolioCTOSchema = yup.object<PortfolioCTO>().shape({
  accountAddr: yupEthAddrRequired
    .label('Account Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'accountAddr',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        description: 'The white-listed blockchain wallet address associated with this account. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  timestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
        description: 'Timestamp Date of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),

  dayNumber: yup
    .number()
    .label('Day Number')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'Day number of timestamp',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  poolHoldings: yup
    .array(PortfolioPoolCTOSchema)
    .required()
    .default([])
    .label('Pool Holdings')
    .meta(
      toFieldMetaData({
        propName: 'poolHoldings',
        description: 'The list of pools held by the account',
        arrayType: ArrayTypeEnum.PORTFOLIO_POOL,
      })
    ),
  accountAssets: yupErc20WeiRequired
    .label('Account Assets')
    .required()
    .default('0')
    .meta(toFieldMetaData({ propName: 'accountAssets', description: 'Account Assets' })),

  portfolioSummaryIncludingAccount: PortfolioSummaryCTOSchema.required()
    .default({})
    .label('Portfolio Summary Including Account')
    .meta(
      toFieldMetaData({
        propName: 'portfolioSummaryIncludingAccount',
        description: 'The summary of the portfolio including the account',
      })
    ),
  portfolioSummaryExcludingAccount: PortfolioSummaryCTOSchema.required()
    .default({})
    .label('Portfolio Summary Excluding Account')
    .meta(
      toFieldMetaData({
        propName: 'portfolioSummaryExcludingAccount',
        description: 'The summary of the portfolio excluding the account',
      })
    ),
})

type PortfolioSummaryCTOInfer = yup.InferType<typeof PortfolioSummaryCTOSchema>

export const _testAPortfolioSummary: PortfolioSummaryCTO = {} as unknown as PortfolioSummaryCTOInfer
export const _testBPortfolioSummary: PortfolioSummaryCTOInfer = {} as unknown as PortfolioSummaryCTO

type PortfolioPoolCTOInfer = yup.InferType<typeof PortfolioPoolCTOSchema>

export const _testAPortfolioPool: PortfolioPoolCTO = {} as unknown as PortfolioPoolCTOInfer
export const _testBPortfolioPool: PortfolioPoolCTOInfer = {} as unknown as PortfolioPoolCTO

type PortfolioCTOInfer = yup.InferType<typeof PortfolioCTOSchema>

export const _testAPortfolio: PortfolioCTO = {} as unknown as PortfolioCTOInfer
export const _testBPortfolio: PortfolioCTOInfer = {} as unknown as PortfolioCTO
